import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  writeBatch,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { writeAuditLog } from '@/services/audit'
import type { IncidentHazard } from '@/types'

export const useHazardsStore = defineStore('hazards', () => {
  const hazards = ref<IncidentHazard[]>([])
  const loading = ref(false)

  let unsubFn: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToHazards(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubFn) return
    if (unsubFn) unsubFn()
    _incidentId = incidentId
    _opId = opId
    loading.value = true
    const q = query(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'hazards'),
      orderBy('order'),
    )
    unsubFn = onSnapshot(q, (snap) => {
      hazards.value = snap.docs.map((d) => ({ hazard_id: d.id, ...d.data() }) as IncidentHazard)
      loading.value = false
    })
  }

  async function addHazard(incidentId: string, opId: string, hazard: string, mitigation: string) {
    const syncStore = useSyncStore()
    syncStore.pendingWrites++
    try {
      const docRef = await addDoc(
        collection(db, 'incidents', incidentId, 'operational_periods', opId, 'hazards'),
        { hazard, mitigation, order: hazards.value.length, created_at: serverTimestamp() },
      )
      await writeAuditLog(incidentId, 'iap.hazard_added', 'hazard', docRef.id, null, { hazard })
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function updateHazard(
    incidentId: string,
    opId: string,
    hazardId: string,
    updates: Partial<Pick<IncidentHazard, 'hazard' | 'mitigation'>>,
  ) {
    const syncStore = useSyncStore()
    syncStore.pendingWrites++
    try {
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'hazards', hazardId),
        updates,
      )
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function removeHazard(incidentId: string, opId: string, hazardId: string) {
    const before = hazards.value.find((h) => h.hazard_id === hazardId) ?? null
    await deleteDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'hazards', hazardId),
    )
    await writeAuditLog(incidentId, 'iap.hazard_removed', 'hazard', hazardId, before as Record<string, unknown> | null, null)
  }

  async function reorderHazards(incidentId: string, opId: string, ordered: IncidentHazard[]) {
    const batch = writeBatch(db)
    ordered.forEach((h, index) => {
      batch.update(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'hazards', h.hazard_id),
        { order: index },
      )
    })
    await batch.commit()
  }

  function teardown() {
    if (unsubFn) { unsubFn(); unsubFn = null }
    _incidentId = ''
    _opId = ''
    hazards.value = []
    loading.value = false
  }

  return { hazards, loading, subscribeToHazards, addHazard, updateHazard, removeHazard, reorderHazards, teardown }
})
